import React, {createContext, useContext, useEffect, useState} from 'react';
import {STORAGE_KEYS} from '../utils/constants';
import {getMe} from '../api/client';
import type {User} from '../types/auth';

interface AuthContextType {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setAuth: (token: string, refreshToken: string, user: User) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({children}: {children: React.ReactNode}): React.JSX.Element {
  const [token, setTokenState] = useState<string | null>(null);
  const [refreshToken, setRefreshTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const storedRefresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const storedSession = localStorage.getItem(STORAGE_KEYS.SESSION);

    const isValidJwt = (t: string) => /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t);

    if (storedToken && isValidJwt(storedToken)) {
      setTokenState(storedToken);
      if (storedRefresh) {
        setRefreshTokenState(storedRefresh);
      }

      // Verify token with backend and get fresh user data
      getMe()
        .then((res) => {
          if (res.user) {
            setUser(res.user as User);
          } else if (storedSession) {
            // Fallback to cached session if API response is unexpected
            try {
              setUser(JSON.parse(storedSession));
            } catch {
              localStorage.removeItem(STORAGE_KEYS.SESSION);
            }
          }
        })
        .catch((err) => {
          console.error('[Auth] Token verification failed:', err);
          // Clear invalid/expired token
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.SESSION);
          setTokenState(null);
          setRefreshTokenState(null);
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // No valid token
      if (storedToken) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.SESSION);
      }
      if (storedSession) {
        try {
          setUser(JSON.parse(storedSession));
        } catch {
          localStorage.removeItem(STORAGE_KEYS.SESSION);
        }
      }
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SESSION);
    }
  }, [user]);

  const setAuth = (newToken: string, newRefresh: string, newUser: User) => {
    localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh);
    setTokenState(newToken);
    setRefreshTokenState(newRefresh);
    setUser(newUser);
  };

  const clearAuth = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    setTokenState(null);
    setRefreshTokenState(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        refreshToken,
        user,
        isLoading,
        isAuthenticated: !!token && !!user,
        setAuth,
        clearAuth,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
